/**
 * App-level JavaScript utilities for the Blazing.UI sample app.
 *
 * Importing the shared interop guard below is what makes this a module rather than a global
 * script - a module is also what the `declare global` augmentation further down requires - so the
 * host page must load it with `<script type="module">` rather than a plain `<script src>`. This
 * file introduces no exported name of its own, so the helpers it publishes on `window` are
 * unchanged.
 */

import { safeInvoke } from "../../Blazing.UI/js/internal/blazing-interop.js";

/** One subscription created by `blazingBreakpoint.watch()`. */
interface BreakpointWatcher {
    /** The media query being watched. */
    readonly mql: MediaQueryList;

    /** The change handler registered on it, kept so `unwatch()` can remove the same reference. */
    readonly handler: (event: MediaQueryListEvent) => void;
}

/** One colour as the sRGB bytes a canvas pixel holds. */
interface FocusColourBytes {
    /** The red channel, 0 to 255. */
    readonly red: number;

    /** The green channel, 0 to 255. */
    readonly green: number;

    /** The blue channel, 0 to 255. */
    readonly blue: number;
}

/** The focus colours the page resolves at one moment. Property names match the .NET record that reads them. */
interface FocusColourSample {
    /** The page ground: the shell's main region background, or the surface token when it paints none. */
    readonly page: FocusColourBytes;

    /** The ring colour composited over the page, its opacity included. */
    readonly indicator: FocusColourBytes;

    /** The ring colour on its own, without its opacity. */
    readonly ring: FocusColourBytes;

    /** The gap stroke between a control and its ring, composited over the page. */
    readonly gap: FocusColourBytes;

    /** The accent fill of a filled control, composited over the page. */
    readonly primary: FocusColourBytes;

    /** The extra stroke composited over the page, or null when no extra stroke is set. */
    readonly border: FocusColourBytes | null;
}

declare global {
    interface Window {
        /**
         * Triggers a browser file download from a data URL or blob URL.
         *
         * @param dataUrl data: or blob: URL of the file content.
         * @param fileName Suggested file name for the download.
         */
        blazingDownloadFile(dataUrl: string, fileName: string): void;

        /**
         * Writes text to the system clipboard using the Async Clipboard API.
         *
         * @param text The text to copy.
         * @returns True on success, false on failure.
         */
        blazingCopyToClipboard(text: string): Promise<boolean>;

        /**
         * Reads a string from localStorage; returns null if absent or unavailable.
         *
         * @param key The storage key to read.
         * @returns The stored value, or null if absent or unavailable.
         */
        blazingLocalGet(key: string): string | null;

        /**
         * Writes a string to localStorage (no-op if unavailable).
         *
         * @param key The storage key to write.
         * @param value The value to store.
         */
        blazingLocalSet(key: string, value: string): void;

        /**
         * Removes a key from localStorage (no-op if unavailable).
         *
         * @param key The storage key to remove.
         */
        blazingLocalRemove(key: string): void;

        /**
         * Resets the shell's scroll to the top. Called on navigation so every page opens at the top.
         *
         * At >=992px the app grid is viewport-locked and the layout's main region is the internal
         * scroll container - interactive Blazor navigation does NOT reset an internal container's
         * scroll the way a full page load resets the window, so a new page would otherwise inherit
         * the previous page's scroll position. Also resets the window for the mobile
         * (natural-scroll) regime. Best-effort.
         *
         * @param isAnimated Whether to glide rather than jump. A reader who asks for reduced motion
         *                   always gets the jump.
         */
        blazingScrollContentToTop(isAnimated?: boolean): void;

        /**
         * Slides the shell's header and footer away as the reader scrolls the content down on a phone,
         * and back as soon as they scroll up. Called once, best-effort.
         */
        blazingWatchShellChrome(): void;

        /**
         * Watches the shell's content region and re-fires a window resize once its width settles
         * at a new value, so components that size themselves from that event re-measure when the
         * sidebar rail collapses or expands without the window moving. Called once, best-effort.
         */
        blazingWatchContentWidth(): void;

        /**
         * Reads the focus colours the page resolves right now, as sRGB bytes.
         *
         * @returns The page, the indicator over the page, the ring, the gap, the accent fill and, when set,
         *          the extra stroke; or null when no canvas is available.
         */
        blazingMeasureFocusColours(): FocusColourSample | null;

        /**
         * Media-query breakpoint helper. Lets a component read the current match state and
         * subscribe to changes so it can switch between desktop and mobile layouts in C#.
         */
        blazingBreakpoint: {
            /** The subscriptions created by `watch()`, keyed by the token returned to the caller. */
            _watchers: Record<number, BreakpointWatcher>;

            /** The next token `watch()` will hand out. */
            _nextToken: number;

            /**
             * @param query A CSS media query, e.g. "(min-width: 1024px)".
             * @returns Whether the query currently matches.
             */
            matches(query: string): boolean;

            /**
             * Subscribes to a media query. Invokes the named .NET method with the new match state
             * on every change.
             *
             * @param query A CSS media query to watch.
             * @param dotNetRef The .NET object to notify on each change.
             * @param methodName The name of the [JSInvokable] method to invoke, taking one `bool`.
             * @returns A token to pass to `unwatch()`.
             */
            watch(query: string, dotNetRef: DotNetObjectReference, methodName: string): number;

            /**
             * Stops a subscription created by `watch()`.
             *
             * @param token The token returned by the `watch()` call to stop.
             */
            unwatch(token: number): void;
        };
    }
}

/**
 * Triggers a browser file download from a data URL or blob URL.
 * Called from PersonalData.razor.cs via IJSRuntime.
 *
 * @param dataUrl data: or blob: URL of the file content.
 * @param fileName Suggested file name for the download.
 */
window.blazingDownloadFile = function (dataUrl: string, fileName: string): void {
    const anchor = document.createElement("a");
    anchor.href = dataUrl;
    anchor.download = fileName;
    anchor.style.display = "none";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
};

/**
 * Copies text by selecting it in a temporary field and running the browser's copy command.
 *
 * @param text The text to copy.
 * @returns True if the browser ran the command, false otherwise.
 */
function copyBySelection(text: string): boolean {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const field = document.createElement("textarea");
    field.value = text;
    field.readOnly = true;
    // 16px keeps iOS Safari from zooming the page when the field takes focus.
    field.style.cssText = "position:fixed;inset-block-start:0;inset-inline-start:-9999px;font-size:16px";
    document.body.appendChild(field);

    try {
        field.focus({ preventScroll: true });
        field.setSelectionRange(0, text.length);

        return document.execCommand("copy");
    } catch {
        return false;
    } finally {
        field.remove();
        previousFocus?.focus({ preventScroll: true });
    }
}

/**
 * Writes text to the system clipboard.
 *
 * Tries the Async Clipboard API first, then a selection copy. Browsers only provide the API on a secure
 * context, so the selection copy is what works on a phone reaching a development host by its LAN address.
 *
 * @param text The text to copy.
 * @returns True on success, false on failure.
 */
window.blazingCopyToClipboard = async function (text: string): Promise<boolean> {
    try {
        if (navigator.clipboard) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch {
        // A refusal from the API can still be answered by the selection copy.
    }

    return copyBySelection(text);
};

/**
 * Reads a string from localStorage; returns null if absent or unavailable.
 *
 * @param key The storage key to read.
 * @returns The stored value, or null if absent or unavailable.
 */
window.blazingLocalGet = function (key: string): string | null {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
};

/**
 * Writes a string to localStorage (no-op if unavailable).
 *
 * @param key The storage key to write.
 * @param value The value to store.
 */
window.blazingLocalSet = function (key: string, value: string): void {
    try {
        localStorage.setItem(key, value);
    } catch {
        /* storage may be unavailable */
    }
};

/**
 * Removes a key from localStorage (no-op if unavailable).
 *
 * @param key The storage key to remove.
 */
window.blazingLocalRemove = function (key: string): void {
    try {
        localStorage.removeItem(key);
    } catch {
        /* storage may be unavailable */
    }
};

/**
 * Resets the shell's scroll to the top. Called on navigation so every page opens at the top.
 * At >=992px the app grid is viewport-locked and the layout's main region is the internal scroll
 * container - interactive Blazor navigation does NOT reset an internal container's scroll the
 * way a full page load resets the window, so a new page would otherwise inherit the previous page's
 * scroll position. Also resets the window for the mobile (natural-scroll) regime. Best-effort.
 *
 * @param isAnimated Whether to glide rather than jump. A reader who asks for reduced motion always
 *                   gets the jump.
 */
window.blazingScrollContentToTop = function (isAnimated = false): void {
    try {
        const isReduced = globalThis.matchMedia("(prefers-reduced-motion: reduce)").matches;
        const behavior: ScrollBehavior = isAnimated && !isReduced ? "smooth" : "auto";
        document.querySelector(".app-layout .blz-layout__main")?.scrollTo({ top: 0, behavior });
        globalThis.scrollTo({ top: 0, behavior });
    } catch {
        /* best-effort - never let a scroll reset surface to the circuit */
    }
};

/**
 * Media-query breakpoint helper. Lets a component read the current match state and
 * subscribe to changes so it can switch between desktop and mobile layouts in C#.
 */
window.blazingBreakpoint = {
    _watchers: {},
    _nextToken: 0,

    /**
     * @param query A CSS media query, e.g. "(min-width: 1024px)".
     * @returns Whether the query currently matches.
     */
    matches(query: string): boolean {
        try {
            return globalThis.matchMedia(query).matches;
        } catch {
            return false;
        }
    },

    /**
     * Subscribes to a media query. Invokes the named .NET method with the new match state on every
     * change.
     *
     * @param query A CSS media query to watch.
     * @param dotNetRef The .NET object to notify on each change.
     * @param methodName The name of the [JSInvokable] method to invoke, taking one `bool`.
     * @returns A token to pass to `unwatch()`.
     */
    watch(query: string, dotNetRef: DotNetObjectReference, methodName: string): number {
        try {
            const mql = globalThis.matchMedia(query);
            // A media query can fire between the component being disposed and unwatch() running, and
            // invoking a disposed .NET reference throws or rejects. safeInvoke tolerates both and
            // stays silent, since by then nothing is listening for the answer anyway.
            const handler = (event: MediaQueryListEvent): void => {
                void safeInvoke(dotNetRef, methodName, undefined, event.matches);
            };
            mql.addEventListener("change", handler);
            const token = ++this._nextToken;
            this._watchers[token] = { mql, handler };
            return token;
        } catch {
            return 0;
        }
    },

    /**
     * Stops a subscription created by `watch()`.
     *
     * @param token The token returned by the `watch()` call to stop.
     */
    unwatch(token: number): void {
        const watcher = this._watchers[token];
        if (watcher) {
            try {
                watcher.mql.removeEventListener("change", watcher.handler);
            } catch {
                /* ignore */
            }
            delete this._watchers[token];
        }
    }
};

// The bespoke sidebar resizer (blazingSidebarResizer) and active-link scroll module
// (blazingSidebarScroll) are retired. BlazingSidebar now owns resize + width persistence
// (in _content/Blazing.UI/js/blazing-sidebar.js) and active-tracking + auto-expand +
// scroll-into-view, so neither sample module is needed.

/**
 * Keeps components that size themselves from a window-resize event honest when the content region
 * changes width on its own.
 *
 * The sidebar rail collapses and expands without the window moving, so anything that measured its
 * container once and wrote pixel geometry keeps the width it had: the demo playgrounds' splitter is
 * the visible case, leaving the preview pane short of the space the rail gave back. Re-firing the
 * event those components already listen for is enough to make them re-measure, and it is observed
 * rather than timed because the rail animates and a fixed delay would either fire early or linger.
 */
window.blazingWatchContentWidth = function (): void {
    try {
        const content = document.querySelector(".app-layout .blz-layout__main");
        if (!content || typeof ResizeObserver === "undefined") {
            return;
        }

        let lastWidth = Math.round(content.getBoundingClientRect().width);
        let pending = 0;

        const observer = new ResizeObserver(() => {
            const width = Math.round(content.getBoundingClientRect().width);
            if (width === lastWidth) {
                return;
            }
            lastWidth = width;

            // Coalesce the frames a width animation produces into one notification, and let the
            // layout settle before measuring so a mid-transition width is never the one published.
            if (pending !== 0) {
                globalThis.clearTimeout(pending);
            }
            pending = globalThis.setTimeout(() => {
                pending = 0;
                globalThis.dispatchEvent(new Event("resize"));
            }, 120);
        });

        observer.observe(content);
    } catch {
        /* ignore */
    }
};

/** How far the reader must travel, in pixels, before a change of scroll direction moves the shell's chrome. */
const CHROME_SCROLL_THRESHOLD = 8;

/** The widths at which the shell's chrome overlays the content and follows the scroll. Matches app.css. */
const CHROME_FOLLOWS_SCROLL_QUERY = "(max-width: 767.98px)";

/**
 * Slides the shell's header and footer away as the reader scrolls the content down on a phone, and back
 * as soon as they scroll up. The state is an attribute on the layout rather than a .NET call, because a
 * scroll gesture would otherwise cost a round trip per direction change; app.css owns the motion.
 *
 * Follows the main region directly: a shell-wide attribute on an element Blazor renders survives its
 * re-renders, since the diff only touches the attributes it wrote.
 */
window.blazingWatchShellChrome = function (): void {
    try {
        const layout = document.querySelector(".app-layout");
        const content = layout?.querySelector(".blz-layout__main");
        if (!layout || !content) {
            return;
        }

        const widthQuery = globalThis.matchMedia(CHROME_FOLLOWS_SCROLL_QUERY);
        let lastOffset = content.scrollTop;
        let isTicking = false;

        const setHidden = (isHidden: boolean): void => {
            layout.toggleAttribute("data-chrome-hidden", isHidden);
        };

        const evaluate = (): void => {
            isTicking = false;
            const offset = content.scrollTop;
            const delta = offset - lastOffset;

            // The top of the page always shows the chrome, however small the last movement was.
            if (offset <= 0) {
                setHidden(false);
                lastOffset = offset;
                return;
            }

            if (Math.abs(delta) < CHROME_SCROLL_THRESHOLD) {
                return;
            }

            setHidden(widthQuery.matches && delta > 0);
            lastOffset = offset;
        };

        content.addEventListener("scroll", () => {
            if (!isTicking) {
                isTicking = true;
                globalThis.requestAnimationFrame(evaluate);
            }
        }, { passive: true });

        // A phone rotated past the breakpoint must not come back with its chrome still away.
        widthQuery.addEventListener("change", () => setHidden(false));
    } catch {
        /* ignore */
    }
};

/** The colour a probe pixel is reset to before each paint, so a colour the canvas rejects reads back as this. */
const UNPAINTED_SENTINEL = "rgb(255 0 255)";

/**
 * Reads the focus colours the page resolves right now, as sRGB bytes, by painting each one onto a canvas
 * over its ground. A computed colour may be reported in oklch or as a relative colour, which a parser
 * would misread as sRGB channels; the canvas resolves it the way the page does, and compositing on the
 * canvas accounts for the ring's opacity.
 *
 * The colours are read from a hidden probe inside the shell's main region, so they are the values the
 * page's own content inherits there, including any focus choice set on the root element.
 *
 * @returns The page, the indicator over the page, the ring, the gap, the accent fill and, when set, the
 *          extra stroke; or null when no canvas is available.
 */
window.blazingMeasureFocusColours = function (): FocusColourSample | null {
    const canvas = document.createElement("canvas");
    canvas.width = 1;
    canvas.height = 1;
    const context = canvas.getContext("2d", { willReadFrequently: true });

    if (!context) {
        return null;
    }

    const paint = (colour: string): void => {
        // A fillStyle the canvas cannot parse is ignored and the previous one kept, so resetting to the
        // sentinel first makes an unreadable colour show up as the sentinel rather than as a stale value.
        context.fillStyle = UNPAINTED_SENTINEL;
        context.fillStyle = colour;
        context.fillRect(0, 0, 1, 1);
    };

    const read = (colour: string, ground: string | null): FocusColourBytes => {
        context.clearRect(0, 0, 1, 1);

        if (ground !== null) {
            paint(ground);
        }

        paint(colour);
        const data = context.getImageData(0, 0, 1, 1).data;

        return { red: data[0] ?? 0, green: data[1] ?? 0, blue: data[2] ?? 0 };
    };

    const isTransparent = (colour: string): boolean => {
        context.clearRect(0, 0, 1, 1);
        paint(colour);

        return (context.getImageData(0, 0, 1, 1).data[3] ?? 0) === 0;
    };

    const host = document.querySelector(".app-layout .blz-layout__main") ?? document.body;
    const probe = document.createElement("div");
    probe.setAttribute("aria-hidden", "true");
    probe.style.cssText = [
        "position: absolute",
        "inline-size: 0",
        "block-size: 0",
        "overflow: hidden",
        "visibility: hidden",
        "pointer-events: none",
        "color: var(--blazing-focus-ring)",
        "caret-color: oklch(from var(--blazing-focus-ring) l c h / 1)",
        "background-color: var(--blazing-focus-ring-gap)",
        "border-block-start-color: var(--blazing-primary)",
        "border-block-end-color: var(--blazing-focus-outline-color)",
        "outline-color: var(--blazing-surface)"
    ].join("; ");
    host.appendChild(probe);

    try {
        const probeStyle = globalThis.getComputedStyle(probe);
        const hostBackground = globalThis.getComputedStyle(host).backgroundColor;

        // The shell paints the page on its main region; anything that paints no background there falls
        // back to the surface token, which is the page colour at the default background level.
        const page = isTransparent(hostBackground) ? probeStyle.outlineColor : hostBackground;

        // The extra stroke's inputs are declared nowhere by the library, so the width is empty unless a
        // host or the user has set one.
        const hasBorder = probeStyle.getPropertyValue("--blazing-focus-border-width").trim() !== "";

        return {
            page: read(page, null),
            indicator: read(probeStyle.color, page),
            ring: read(probeStyle.caretColor, null),
            gap: read(probeStyle.backgroundColor, page),
            primary: read(probeStyle.getPropertyValue("border-block-start-color"), page),
            border: hasBorder ? read(probeStyle.getPropertyValue("border-block-end-color"), page) : null
        };
    } finally {
        probe.remove();
    }
};